import { HttpClient } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { Observable, of } from "rxjs"
import { finalize, map, shareReplay, tap } from "rxjs/operators"
import { environment } from "../../environments/environment"
import { titleCaseCityName, UsCity, UsState } from "../models/city.model"

const CACHE_TTL_MS = 30 * 60 * 1000

export interface CreateCityResult {
  status: string
  city: UsCity
}

@Injectable({
  providedIn: "root",
})
export class CityService {
  private readonly apiUrl = `${environment.apiUrl}/cities`
  private readonly statesApiUrl = `${environment.apiUrl}/cities/states`
  private readonly searchCache = new Map<string, { expiresAt: number; cities: UsCity[] }>()
  private readonly statesCache = new Map<string, { expiresAt: number; states: UsState[] }>()
  private readonly inFlight = new Map<string, Observable<UsCity[]>>()
  private readonly statesInFlight = new Map<string, Observable<UsState[]>>()

  constructor(private http: HttpClient) {}

  searchCities(search = "", limit = 50): Observable<UsCity[]> {
    const trimmed = search.trim()
    if (!trimmed) {
      return of([])
    }

    const normalizedSearch = trimmed.toLowerCase()
    const cacheKey = `${normalizedSearch}|${limit}`
    const cached = this.searchCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now()) {
      return of(cached.cities)
    }

    const existing = this.inFlight.get(cacheKey)
    if (existing) {
      return existing
    }

    const params: Record<string, string> = {
      limit: String(Math.min(Math.max(limit, 1), 100)),
      search: trimmed,
    }

    const request$ = this.http.get<UsCity[] | { cities?: UsCity[] }>(this.apiUrl, { params }).pipe(
      map((body) => this.normalizeCities(body)),
      tap((cities) => {
        this.searchCache.set(cacheKey, { cities, expiresAt: Date.now() + CACHE_TTL_MS })
      }),
      finalize(() => {
        this.inFlight.delete(cacheKey)
      }),
      shareReplay(1),
    )

    this.inFlight.set(cacheKey, request$)
    return request$
  }

  searchStates(search = "", limit = 50): Observable<UsState[]> {
    const trimmed = search.trim()
    const normalizedSearch = trimmed.toLowerCase()
    const cacheKey = `${normalizedSearch}|${limit}`
    const cached = this.statesCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now()) {
      return of(cached.states)
    }

    const existing = this.statesInFlight.get(cacheKey)
    if (existing) {
      return existing
    }

    const params: Record<string, string> = {
      limit: String(Math.min(Math.max(limit, 1), 100)),
    }
    if (trimmed) {
      params["search"] = trimmed
    }

    const request$ = this.http.get<UsState[]>(this.statesApiUrl, { params }).pipe(
      map((body) => (Array.isArray(body) ? body.map((state) => this.normalizeState(state)) : [])),
      tap((states) => {
        this.statesCache.set(cacheKey, { states, expiresAt: Date.now() + CACHE_TTL_MS })
      }),
      finalize(() => {
        this.statesInFlight.delete(cacheKey)
      }),
      shareReplay(1),
    )

    this.statesInFlight.set(cacheKey, request$)
    return request$
  }

  createCity(name: string, state: string): Observable<UsCity> {
    const payload = {
      name: titleCaseCityName(name),
      state: (state || "").trim(),
    }
    return this.http.post<CreateCityResult>(this.apiUrl, payload).pipe(
      map((body) => this.normalizeCity(body.city)),
      tap(() => this.clearCache()),
    )
  }

  findCityByName(name: string, stateCode?: string | null): Observable<UsCity | null> {
    const trimmed = (name || "").trim()
    if (!trimmed) {
      return of(null)
    }
    return this.searchCities(trimmed, 25).pipe(
      map((cities) => {
        const exact = cities.filter((c) => c.name.toLowerCase() === trimmed.toLowerCase())
        if (stateCode) {
          const withState = exact.find((c) => c.stateCode?.toUpperCase() === stateCode.toUpperCase())
          if (withState) return withState
        }
        if (exact.length === 1) return exact[0]
        return exact[0] ?? cities[0] ?? null
      }),
    )
  }

  clearCache(): void {
    this.searchCache.clear()
    this.statesCache.clear()
    this.inFlight.clear()
    this.statesInFlight.clear()
  }

  private normalizeCities(body: UsCity[] | { cities?: UsCity[] } | null | undefined): UsCity[] {
    if (Array.isArray(body)) {
      return body.map((c) => this.normalizeCity(c))
    }
    if (body && Array.isArray(body.cities)) {
      return body.cities.map((c) => this.normalizeCity(c))
    }
    return []
  }

  private normalizeCity(raw: UsCity): UsCity {
    return {
      id: raw.id ?? null,
      name: raw.name,
      state: raw.state ?? null,
      stateCode: raw.stateCode ?? (raw as unknown as { state_code?: string }).state_code ?? null,
    }
  }

  private normalizeState(raw: UsState): UsState {
    return {
      name: raw.name ?? raw.state,
      state: raw.state ?? raw.name,
      stateCode: raw.stateCode ?? (raw as unknown as { state_code?: string }).state_code ?? "",
    }
  }
}
